import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { BLOG_POSTS, getPostMeta } from "@/lib/blog";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function LatestFromBlog({ limit = 3 }) {
  const posts = [...BLOG_POSTS]
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO))
    .slice(0, limit);

  if (posts.length === 0) return null;

  return (
    <section className="bg-slate-50 py-20 md:py-24" data-testid="home-latest-blog">
      <div className="fg-container">
        <div className="flex flex-col items-center gap-4 text-center md:flex-row md:items-end md:justify-between md:text-left">
          <div>
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Latest from the <span className="text-blue-600">Blog</span>
            </h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Honest, useful plumbing advice written by the licensed plumbers who do the work — no fluff, no upsells.
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="home-blog-all-link"
            className="hidden flex-shrink-0 items-center gap-2 rounded-full border-2 border-slate-900/80 px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-900 transition-colors hover:bg-slate-900 hover:text-white md:inline-flex"
          >
            View All Posts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => {
            const { service, suburb } = getPostMeta(p);
            const cover = p.coverImage || service?.image;
            return (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`home-blog-card-${p.slug}`}
                className="fg-card-hover group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white"
              >
                {cover && (
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={cover}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
                    {suburb && <span>{suburb.name}</span>}
                    {service && suburb && <span className="text-slate-300">·</span>}
                    {service && <span className="text-slate-500">{service.title}</span>}
                  </div>
                  <h3 className="mt-3 font-display text-xl font-bold leading-snug tracking-tight text-slate-900 group-hover:text-blue-700">
                    {p.title}
                  </h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" /> {formatDate(p.dateISO)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> {p.readingMins} min
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-blue-700">
                      Read <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center md:hidden">
          <Link
            to="/blog"
            data-testid="home-blog-all-link-mobile"
            className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900/80 px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"
          >
            View All Posts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
